// GlassButton — primary (gradient) / secondary (glass) / ghost. Spring press.
// BARE-RN: expo-linear-gradient → react-native-linear-gradient; @expo/vector-icons
// Ionicons → local Icon shim (react-native-vector-icons/Ionicons).
import React, { useRef } from 'react';
import {
  Animated, Pressable, Text, ActivityIndicator, StyleSheet, View, StyleProp, ViewStyle,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons, IoniconName } from '../ui/Icon';
import GlassSurface from './GlassSurface';
import { glassTokens } from './tokens';
import { colors, fonts } from '../../constants/theme';

export interface GlassButtonProps {
  title: string;
  onPress?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: IoniconName;
  style?: StyleProp<ViewStyle>;
}

const H = { sm: 38, md: 46, lg: 54 };
const F = { sm: 14, md: 15, lg: 16 };

export default function GlassButton({
  title, onPress, variant = 'primary', size = 'md',
  loading = false, disabled = false, fullWidth = false, icon, style,
}: GlassButtonProps) {
  const scale = useRef(new Animated.Value(1)).current;
  const pressIn = () => Animated.spring(scale, { toValue: glassTokens.pressScale, useNativeDriver: true, speed: 50, bounciness: 0 }).start();
  const pressOut = () => Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 40, bounciness: 6 }).start();
  const off = disabled || loading;
  const height = H[size];
  const fontSize = F[size];

  const Label = (
    loading ? <ActivityIndicator color={variant === 'primary' ? '#FFFFFF' : colors.primary} size="small" />
      : (
        <View style={styles.row}>
          {icon ? <Ionicons name={icon} size={fontSize + 2} color={variant === 'primary' ? '#FFFFFF' : colors.primary} /> : null}
          <Text style={[styles.text, { fontSize, color: variant === 'primary' ? '#FFFFFF' : colors.primary }]} numberOfLines={1}>{title}</Text>
        </View>
      )
  );

  // Secondary uses the shared glass surface (one implementation).
  if (variant === 'secondary') {
    return (
      <GlassSurface
        variant="pill" radius={glassTokens.radius.pill} onPress={off ? undefined : onPress} disabled={off}
        style={[fullWidth && styles.full, style]}
        contentStyle={[styles.box, { height }]}
        accessibilityLabel={title}
      >
        {Label}
      </GlassSurface>
    );
  }

  return (
    // Outer view carries the shadow + press-scale. Full width via alignSelf:stretch
    // (Yoga applies it pre-transform; width:'100%' on a transformed view was
    // unreliable under Fabric and rendered the button narrower than the inputs).
    <Animated.View style={[{ transform: [{ scale }] }, variant === 'primary' && styles.primaryShadow, fullWidth && styles.full, off && { opacity: glassTokens.disabledOpacity }, style]}>
      <Pressable
        style={[styles.box, { height }, fullWidth && styles.full]}
        onPress={off ? undefined : onPress}
        onPressIn={off ? undefined : pressIn}
        onPressOut={off ? undefined : pressOut}
        disabled={off}
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{ disabled: off, busy: loading }}
      >
        {/* Background fill (absolute so the Pressable owns layout/centering). */}
        {variant === 'primary' ? (
          <LinearGradient colors={[colors.primary, colors.primaryLight]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} />
        ) : (
          <View style={[StyleSheet.absoluteFill, styles.ghost]} />
        )}
        {Label}
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  full: { alignSelf: 'stretch', width: '100%' },
  // The Pressable IS the button box: centers the label both axes; overflow hidden
  // clips the absolute gradient to the pill.
  box: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 22,
    borderRadius: glassTokens.radius.pill,
    overflow: 'hidden',
  },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  text: { ...fonts.semibold, textAlign: 'center' },
  primaryShadow: {
    shadowColor: colors.primary, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.32, shadowRadius: 10, elevation: 5,
  },
  ghost: { backgroundColor: 'transparent' },
});
